import React, { Component } from 'react';
import '../../../App.scss';
import 'antd/dist/antd.css';
import { Empty, PageHeader, Space, DatePicker, Button } from 'antd';
import moment from 'moment';

import { AdminClientsList, ClientsSearch } from '../../../components/admin/AdminClientsComponents';
import AdminAllClientsModel from '../../../models/AdminAllClientsModel';
import CenteredSpinner from '../../../components/CenteredSpinner';

const { RangePicker } = DatePicker;

function escapeCsvValue(value) {
  const text = value === null || value === undefined ? '' : `${value}`;
  if (text.includes('"') || text.includes(',') || text.includes('\n')) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function toIsoString(ts) {
  if (!ts) return '';
  if (typeof ts.toDate === 'function') return ts.toDate().toISOString();
  if (typeof ts.toMillis === 'function') return new Date(ts.toMillis()).toISOString();
  if (ts instanceof Date) return ts.toISOString();
  return '';
}

// /client/manage/profile
export default class AdminClientsScreen extends Component {
  constructor(props) {
    super(props);
    this.model = new AdminAllClientsModel({ viewModel: this });

    this.state = {
      lastUpdatedId: null,
      clients: null,
      loading: true,
      searching: false,
      searchText: '',
      isSearchDisabled: false,
      dateRange: [null, null],
      filteredClients: null,
    };
  }

  componentDidMount() {
    this.model.waitForClientsData();
  }

  componentWillUnmount() {
    if (this.model) this.model.stop();
  }

  isLoading = () => {
    return this.state.loading || false;
  }

  updatedClientsDataSource = (clients, id) => {
    this.setState(
      {
        lastUpdatedId: id,
        clients,
        loading: false,
      },
      () => this.applyFilters(this.state.searchText, this.state.dateRange)
    );
  }

  applyFilters = (searchText, dateRange) => {
    const list = this.state.clients || [];
    const lowerSearchText = (searchText || '').toLowerCase().trim();
    const hasSearch = lowerSearchText.length > 0;
    const start = dateRange && dateRange[0] ? dateRange[0].startOf('day').valueOf() : null;
    const end = dateRange && dateRange[1] ? dateRange[1].endOf('day').valueOf() : null;

    const filtered = list.filter((client) => {
      const createdAtMillis = client.createdAt && client.createdAt.toMillis ? client.createdAt.toMillis() : null;
      const inDateRange = (!start || (createdAtMillis && createdAtMillis >= start))
        && (!end || (createdAtMillis && createdAtMillis <= end));
      if (!inDateRange) return false;
      if (!hasSearch) return true;

      const lowerEmail = (client.email || '').toLowerCase();
      const lowerDisplayName = (client.displayName || '').toLowerCase();
      const lowerPhoneNumber = (client.phoneNumber || '').toLowerCase();
      const lowerEmployerName = (client.employerName || '').toLowerCase();
      const lowerProfileBio = (client.profileBio || '').toLowerCase();
      const lowerAuthProvider = (client.authProvider || '').toLowerCase();
      const lowerAuthProviders = (Array.isArray(client.authProviders) ? client.authProviders.join(' ') : '').toLowerCase();
      const lowerOriginReason = (client.originReason || '').toLowerCase();
      const lowerOriginPath = (client.originPath || '').toLowerCase();

      return (
        lowerEmail.includes(lowerSearchText) ||
        lowerDisplayName.includes(lowerSearchText) ||
        lowerPhoneNumber.includes(lowerSearchText) ||
        lowerEmployerName.includes(lowerSearchText) ||
        lowerProfileBio.includes(lowerSearchText) ||
        lowerAuthProvider.includes(lowerSearchText) ||
        lowerAuthProviders.includes(lowerSearchText) ||
        lowerOriginReason.includes(lowerSearchText) ||
        lowerOriginPath.includes(lowerSearchText)
      );
    });

    this.setState({
      searching: hasSearch,
      filteredClients: (hasSearch || start || end) ? filtered : null,
      isSearchDisabled: false,
      loading: false,
    });
  }

  onSearch = (searchText) => {
    this.setState(
      {
        searchText: searchText || '',
      },
      () => this.applyFilters(this.state.searchText, this.state.dateRange)
    );
  }

  onDateRangeChange = (range) => {
    const dateRange = range || [null, null];
    this.setState(
      {
        dateRange,
      },
      () => this.applyFilters(this.state.searchText, this.state.dateRange)
    );
  }

  exportClientsCsv = () => {
    const exportList = this.state.filteredClients || this.state.clients || [];
    const rows = [
      [
        'clientId',
        'displayName',
        'email',
        'status',
        'type',
        'categoryCode',
        'categoryName',
        'marketingOptIn',
        'createdAt',
        'updatedAt',
        'authProvider',
        'platform',
        'originReason',
        'originPath',
      ],
      ...exportList.map((client) => [
        client.id || '',
        client.displayName || '',
        client.email || '',
        client.status || '',
        client.type || '',
        client.categoryCode || '',
        client.categoryName || '',
        client.marketingOptIn === true ? 'true' : client.marketingOptIn === false ? 'false' : '',
        toIsoString(client.createdAt),
        toIsoString(client.updatedAt),
        client.authProvider || '',
        client.platform || '',
        client.originReason || '',
        client.originPath || '',
      ]),
    ];
    const csv = rows.map((row) => row.map(escapeCsvValue).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `admin-clients-${moment().format('YYYYMMDD-HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  renderFilters = () => {
    const hasRows = !!((this.state.filteredClients || this.state.clients || []).length);
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <ClientsSearch
          initialValue={this.state.searchText}
          callbackOnSearch={this.onSearch}
          loading={this.state.searching}
          disabled={this.state.isSearchDisabled}
        />
        <Space>
          <RangePicker value={this.state.dateRange} onChange={this.onDateRangeChange} allowClear />
          <Button onClick={this.exportClientsCsv} disabled={!hasRows}>Export CSV</Button>
        </Space>
      </Space>
    );
  }

  render() {
    if (!this.state.loading && this.state.clients && this.state.clients.length > 0) {
      return (
        <div style={{ height: '100%', textAlign: 'center' }}>
          <PageHeader ghost={false} title="Clients List" />
          {this.renderFilters()}
          <AdminClientsList
            condition={() => !this.state.loading}
            clients={this.state.filteredClients || this.state.clients}
          />
        </div>
      );
    }

    if (!this.state.loading && (!this.state.clients || this.state.clients.length === 0)) {
      return (
        <div style={{ height: '100%', textAlign: 'center' }}>
          <PageHeader ghost={false} title="Clients List" />
          {this.renderFilters()}
          <Empty />
        </div>
      );
    }

    return (
      <div style={{ height: '100%', textAlign: 'center' }}>
        <PageHeader ghost={false} title="Clients List" />
        <Space direction="vertical" style={{ width: '100%' }}>
          {this.renderFilters()}
          <CenteredSpinner loading={this.isLoading()} noLayout={false} />
        </Space>
      </div>
    );
  }
}